/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MessageService } from '@src/app/shared/message.service';
import { Txn } from '@src/app/model/txn';
import { Field } from '@src/app/shared/field/Field';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { Cycle } from '@src/app/model/cycle';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';

@Injectable({
    providedIn: 'root'
})
export class ArTxnService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);
    protected unitSvc = inject(UnitService);
    personSvc = inject(PersonService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    protected bCodeSvc = inject(BCodeService);


    protected get baseUrl(): string {
        return this.getAjaxPath(Txn.LEDGER.AR.code);
    }
    protected cache: Txn[];
    protected typeString = 'Txn';

    getFields(): Field[] {

        return [
            FieldMaker.id(),
            FieldMaker.idHolder('refNr'), // Just for search
            FormNavLinkComponent.makeTxnLink('Ref'),
            FieldMaker.rev(),
            FieldMaker.idHolder('billingId'),
            FormPicklistComponent.make('Type', 'typeId', 'type', { items: [Txn.TYPE.INVOICE, Txn.TYPE.CREDIT_NOTE, Txn.TYPE.BANK_IN] }),
            FormDateComponent.make('Transaction Date', 'txnDate', { cellOpts: { width: '2%' }, formColumn: 2 }),
            FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle', { service: this.cycleSvc }, { formColumn: 2 }),
            FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod', { service: this.periodSvc }, {
                formColumn: 2,
                refresh: (o: Cycle, control: AppPicklistControl) => {
                    if (o) {
                        control.field.setPicklistItems(o.periods);
                        control.setValue(null);
                    }
                }
            }),

            FormPicklistComponent.make('Account', 'bCodeId', 'bCode', { service: this.bCodeSvc }, { formColumn: 3 }),

            FormNavLinkComponent.makeDetail('Unit', 'unit.name','unitId',
                UnitPageNavRoute, true
            ),
            FormPicklistComponent.make('Unit Name', 'unitId', 'unit.name', {selfPopulate: true}, {visible: Field.noShow}), // Just for search
            FormNavLinkComponent.makeDetail('Owner', 'ownerName','personId',
                PersonPageNavRoute, true).override({ calculateValue: (o: Txn) => o.person?.fullName }
            ),
            FormNumberComponent.make("Debit", "debit", { format: 'currency', width: 9, formatParms: '1.2-2' }, { formColumn: 4 }),
            FormNumberComponent.make("Credit", "credit", { format: 'currency', width: 9, formatParms: '1.2-2' }, { formColumn: 4 }),
            FormNumberComponent.make("Outstanding", "outstanding", { format: 'currency', width: 9, formatParms: '1.2-2' }, { formColumn: 4 }),

            new GridField({
                field:
                    { label: 'Details', value: 'details', visible: Field.formOnly, formRow: 2 },
                rowFactory: () => [
                    FieldMaker.id({ label: 'Ref', visible: { phone: true, computer: true, form: true }, footer: { text: 'Totals', style: 'text-align: right' } }),
                    FormPicklistComponent.make('Account', 'bCodeId', 'bCode',
                        { service: this.bCodeSvc },
                        { cellOpts: { width: '50%' } }
                    ),
                    FormNumberComponent.make("Debit", "debit", { format: 'currency', width: 9, formatParms: '1.2-2' }),
                    FormNumberComponent.make("Credit", "credit", { format: 'currency', width: 9, formatParms: '1.2-2' }),
                    FieldMaker.notes(),
                    FieldMaker.rev(),
                    FieldMaker.deleteGridRow(),
                ],
                hasFooter: true
            }),
        ];
    }

}
